// Vendor comparison page component — audit D1.4 / D5.3.
//
// Honest, additive comparison pages for the two most-searched
// alternatives Zynthoro competes with. The audit calls this out
// explicitly: "Buyers evaluating their first ERP compare against named
// alternatives, and the sitemap shows no comparison page exists."
//
// Each page passes a data object into this shared layout. The copy
// stays plain, specific and in the SME buyer's vocabulary — no
// marketing-speak.

import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { ArrowRight, Check, Minus } from "lucide-react";

export default function ComparisonPage({ data }) {
  useEffect(() => {
    document.title = data.title;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", data.description);
  }, [data.title, data.description]);

  return (
    <>
      <Navbar />
      <main data-testid={`page-vs-${data.slug}`}>
        {/* Hero */}
        <section className="zy-section" style={{ paddingTop: 96, paddingBottom: 48 }}>
          <div className="max-w-4xl mx-auto px-6">
            <p className="zy-eyebrow mb-3">Honest comparison</p>
            <h1 className="zy-h1" data-testid="vs-hero-headline">
              {data.heroHeadline}
            </h1>
            <p className="zy-body mt-5 max-w-2xl">{data.heroIntro}</p>
            <div className="mt-8 flex flex-col sm:flex-row items-start gap-4">
              <Link to="/signup?trial=1" className="zy-btn-primary" data-testid="vs-cta-demo">
                Explore the 7-day demo <ArrowRight size={16} />
              </Link>
              <Link to="/pricing" className="zy-btn-outline" data-testid="vs-cta-pricing">
                See Zynthoro pricing
              </Link>
            </div>
          </div>
        </section>

        {/* At-a-glance pricing */}
        <section className="zy-section bg-[#FAFAFB]" aria-labelledby="vs-pricing-title">
          <div className="max-w-4xl mx-auto px-6">
            <h2 id="vs-pricing-title" className="zy-h2">
              What each one actually costs
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              <article className="rounded-2xl border border-[#eef1f7] bg-white p-7">
                <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#0A1628]/60">
                  Zynthoro
                </p>
                <p className="mt-3 text-[26px] font-bold tracking-tight text-[#0A1628] leading-tight">
                  {data.pricing.zynthoro.price}
                </p>
                <p className="mt-2 text-[14px] text-[#0A1628]/70 leading-relaxed">
                  {data.pricing.zynthoro.note}
                </p>
              </article>
              <article className="rounded-2xl border border-[#eef1f7] bg-white p-7">
                <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#0A1628]/60">
                  {data.competitor}
                </p>
                <p className="mt-3 text-[26px] font-bold tracking-tight text-[#0A1628] leading-tight">
                  {data.pricing.competitor.price}
                </p>
                <p className="mt-2 text-[14px] text-[#0A1628]/70 leading-relaxed">
                  {data.pricing.competitor.note}
                </p>
              </article>
            </div>
            <p className="mt-5 text-[12.5px] text-[#0A1628]/50">
              {data.pricing.disclaimer}
            </p>
          </div>
        </section>

        {/* Feature matrix */}
        <section className="zy-section bg-white" aria-labelledby="vs-matrix-title">
          <div className="max-w-4xl mx-auto px-6">
            <h2 id="vs-matrix-title" className="zy-h2">
              Side-by-side on what SMEs actually need
            </h2>
            <div className="mt-8 overflow-x-auto rounded-2xl border border-[#eef1f7] bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#FAFAFB] border-b border-[#eef1f7]">
                    <th className="text-left px-5 py-3 font-semibold text-[#0A1628]">What matters</th>
                    <th className="text-left px-5 py-3 font-semibold text-[#0A1628]">Zynthoro</th>
                    <th className="text-left px-5 py-3 font-semibold text-[#0A1628]">{data.competitor}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.matrix.map((row, i) => (
                    <tr key={row.feature} className={i % 2 ? "bg-[#FAFAFB]" : "bg-white"}>
                      <td className="px-5 py-3 font-medium text-[#0A1628]">{row.feature}</td>
                      <td className="px-5 py-3 text-[#0A1628]/80">
                        <Cell value={row.zynthoro} />
                      </td>
                      <td className="px-5 py-3 text-[#0A1628]/80">
                        <Cell value={row.competitor} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Migration paragraph */}
        <section className="zy-section bg-[#FAFAFB]" aria-labelledby="vs-migration-title">
          <div className="max-w-3xl mx-auto px-6">
            <h2 id="vs-migration-title" className="zy-h2">
              Switching from {data.competitor} to Zynthoro
            </h2>
            <p className="zy-body mt-5">{data.migration}</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="zy-section bg-white" aria-labelledby="vs-faq-title">
          <div className="max-w-3xl mx-auto px-6">
            <h2 id="vs-faq-title" className="zy-h2">
              Frequently asked
            </h2>
            <div className="mt-7 space-y-3">
              {data.faq.map((item, i) => (
                <details
                  key={item.q}
                  className="group rounded-xl border border-[#eef1f7] bg-white"
                  data-testid={`vs-faq-${i + 1}`}
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-left font-semibold text-[#0A1628]">
                    {item.q}
                  </summary>
                  <div className="border-t border-[#edf0f5] px-5 py-4 text-[14px] leading-6 text-slate-600">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
            <p className="mt-10 text-[13.5px] text-[#0A1628]/60">
              Still comparing?{" "}
              <Link to="/pricing" className="font-semibold text-[#202B60] hover:underline">
                See the full Zynthoro pricing page
              </Link>
              .
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function Cell({ value }) {
  if (value === true) return <Check size={16} className="text-[#202B60]" aria-label="Yes" />;
  if (value === false) return <Minus size={16} className="text-[#0A1628]/30" aria-label="No" />;
  return <span>{value}</span>;
}
